import { PageWrapper, Meta, Custom404 } from '../../../../components';
import {
  getListingsEnvironmentBaseUrl,
  capitalizeAllFirstLetters,
  RepliersStateToAbbreviatedProvinceMap,
} from '@requity-homes/utils';
import { useRouter } from 'next/router';

import { SearchListingsDocument } from '../../../../graphql/generated';
import {
  queryParamsToCityMap,
  fetchUserLocationByIP,
  prepareSearchFilterValues,
  getFilterValuesFromQuery,
} from '../../../../utils';
import { client } from '../../../../graphql/apollo-client';
import { GetServerSidePropsContext } from 'next';
import * as React from 'react';
import { ScrollableListingsList } from '../../../../components/scrollable-listings-list';
import { validateSearchQueryParams } from '../../../../utils/validate-search-query-params';

const repliersCdnUrl = process.env.NEXT_PUBLIC_REPLIER_CDN_URL;

const DEFAULT_CITY = 'edmonton';
const DEFAULT_PROVINCE = 'ab';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  let city = context.params.city as string;
  let province = context.params.province as string;

  if (city === 'user-location') {
    const forwarded = context.req.headers['x-forwarded-for']?.toString();
    const ip = forwarded
      ? forwarded.split(/, /)[0]
      : context.req.socket.remoteAddress;

    console.log('Forwarded:', forwarded);
    console.log('IP:', ip);

    try {
      ({ city, region: province } = await fetchUserLocationByIP(
        ip,
        process.env.IPINFO_TOKEN,
      ));
    } catch (error) {
      console.error('Error fetching user location by IP:', error);
      city = DEFAULT_CITY;
      province = DEFAULT_PROVINCE;
    }

    try {
      city = city.toLowerCase().replace(/ /g, '-');

      province = RepliersStateToAbbreviatedProvinceMap[province];

      // validate the city and province
      console.log('Validating city and province:', city, province);
      const statusCode = validateSearchQueryParams(city, province);

      if (statusCode == 404) {
        console.error('Invalid city or province:', city, province);

        city = DEFAULT_CITY;
        province = DEFAULT_PROVINCE;
      } else {
        console.log('City and province are valid:', city, province);
      }
    } catch (error) {
      console.error(
        'Error validating city and province:',
        error,
        city,
        province,
      );
      city = DEFAULT_CITY;
      province = DEFAULT_PROVINCE;
    }

    context.res.setHeader('Location', `/${province}/${city}/widget`);
    context.res.statusCode = 302;
    context.res.end();
    return { props: {} };
  }

  const { query } = context;

  const filterValues = getFilterValuesFromQuery(query, city);

  const { data } = await client.query({
    query: SearchListingsDocument,
    variables: { filterValues: prepareSearchFilterValues(filterValues) },
  });
  const { listings, clusters, ...rest } = data.searchListings || {};

  const statusCode = validateSearchQueryParams(query.city, query.province);
  context.res.statusCode = statusCode;

  return {
    props: {
      city: queryParamsToCityMap[city],
      filterValues,
      listings,
      clusters,
      listingsStats: rest,
      statusCode,
    },
  };
}

export default function WidgetPage({ listings, city, statusCode }) {
  const router = useRouter();
  const listingsData = {
    listings: listings || [],
  };

  const baseUrl = `${getListingsEnvironmentBaseUrl(
    process.env.NEXT_PUBLIC_STAGE,
  )}${router.pathname}`.split('/[province]/')[0];

  if (statusCode == 404) {
    return <Custom404 />;
  }

  let ogUrl = '';
  let province = '';
  if (listingsData.listings.length > 0) {
    const firstListing = listingsData.listings[0];
    province = firstListing.address.state;
    ogUrl = `${baseUrl}/${
      RepliersStateToAbbreviatedProvinceMap[province]
    }/${city.toLowerCase().replace(/ /g, '-')}/rent-to-own`;
  }

  const pageTitle = `Rent-to-Own Homes in ${capitalizeAllFirstLetters(
    city,
  )}, ${capitalizeAllFirstLetters(province)} | Requity Homes`;

  return (
    <PageWrapper>
      <Meta
        title={pageTitle}
        description={`View properties and rent-to-own listings for sale in ${capitalizeAllFirstLetters(
          city,
        )}, ${province}. Find the best rent-to-own homes near you with down payments starting as low as 2%.`}
        ogImage="https://brand-assets.requityhomes.com/open-graph-city-image.png"
        keywords={`Rent to Own, Homes, ${city}`}
        ogUrl={ogUrl}
      />
      <ScrollableListingsList
        listings={listingsData.listings}
        repliersCdnUrl={repliersCdnUrl}
      />
    </PageWrapper>
  );
}
